import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatINR } from '@vj/shared';
import { api, downloadFile } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField } from '../../components/ui/Field.jsx';
import { todayIso } from './dateRange.js';

function inNDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function ChitDuesPage() {
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(inNDays(7));
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'chit-dues', from, to],
    queryFn: () => api.get('/reports/chit-dues', { query: { from, to } }),
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/chit-dues', 'chit-dues.csv', { query: { from, to } });
    } finally {
      setDownloading(false);
    }
  };

  const rows = data?.rows ?? [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Chit dues</h1>
          <p className="mt-1 text-sm text-muted">Installments due or overdue in the selected period.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      <div className="card table-scroll">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Scheme</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 text-right">Installment #</th>
              <th className="px-4 py-3">Due date</th>
              <th className="px-4 py-3 text-right">Due</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
            ) : !rows.length ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Nothing due in this period.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={`${r.scheme_no}-${r.month_number}`} className="tabular border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">
                    {r.scheme_no}
                    <div className="text-xs text-muted">{r.schemeName}</div>
                  </td>
                  <td className="px-4 py-3">
                    {r.customer_name}
                    {r.customer_mobile ? <div className="text-xs text-muted">{r.customer_mobile}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-right">{r.month_number}</td>
                  <td className="px-4 py-3">{r.due_date}</td>
                  <td className="px-4 py-3 text-right">{formatINR(Number(r.amount_due) - Number(r.amount_paid))}</td>
                  <td className="px-4 py-3">
                    <span className={r.status === 'overdue' ? 'font-semibold text-danger' : 'text-warning'}>
                      {r.status === 'overdue' ? 'Overdue' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
